/**
 * El idioma: **un botón que rota**, no tres.
 *
 * Antes era una lista con los tres nombres escritos enteros —«Castellano Català English»— y vivía
 * al pie de la pantalla del alumnado, debajo del campo del PIN. Ocupaba una fila entera del móvil
 * justo donde solo hay que hacer una cosa, y en la sala llegaba a desbordar la página a lo ancho.
 * Aquí es un solo objetivo, con el código de dos letras y un globo, y vive en la cabecera, que es
 * donde se busca.
 *
 * **Un botón que rota tiene que decir a dónde va**, o quien no ve la pantalla pulsa a ciegas. El
 * texto visible es el idioma de ahora (`ES`) y el nombre accesible es la frase entera: «Idioma:
 * Castellano. Cambiar a Català». Así se anuncia el estado y la consecuencia, que es lo que una
 * lista de tres opciones daba gratis y esto hay que darlo a mano.
 *
 * Cuando hay sesión, la elección se guarda en el perfil escribiendo **directamente** contra
 * PostgREST: `profiles` no es una tabla de partida, así que el principio VI no lo prohíbe, y dar
 * un rodeo por el servidor para cambiar de idioma sería un salto de red sin nada a cambio. El
 * privilegio por columna impide que por esa vía se toque el rol o el veto.
 */

import { useTranslation } from 'react-i18next';
import { LOCALES, type Locale } from '@clica/shared';
import { applyLocale, isLocale } from '../i18n/index.js';
import { supabase } from '../auth/supabase.js';
import { useSession } from '../auth/session.js';
import { Glifo } from './sistema/glifo.js';

const PRIMERO: Locale = LOCALES[0];

/** El siguiente de la rueda, volviendo al principio. */
function siguienteIdioma(actual: Locale): Locale {
  const indice = LOCALES.indexOf(actual);
  return LOCALES[(indice + 1) % LOCALES.length] ?? PRIMERO;
}

export function SelectorIdioma({
  className,
}: { className?: string | undefined } = {}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const state = useSession((store) => store.state);
  const setProfile = useSession((store) => store.setProfile);

  const actual: Locale = isLocale(i18n.resolvedLanguage) ? i18n.resolvedLanguage : PRIMERO;
  const siguiente = siguienteIdioma(actual);

  async function rotar(): Promise<void> {
    await applyLocale(siguiente);

    if (state.status !== 'active') return;

    const { error } = await supabase
      .from('profiles')
      .update({ locale: siguiente })
      .eq('id', state.profile.id);

    if (error !== null) {
      // No se revierte la interfaz: el idioma ya ha cambiado y es lo que la persona pidió. Lo
      // que se pierde es la persistencia, y se sabrá al volver a entrar.
      console.error('No se ha podido guardar el idioma en el perfil', error);
      return;
    }

    setProfile({ ...state.profile, locale: siguiente });
  }

  return (
    <button
      type="button"
      onClick={() => void rotar()}
      // `min-h-11 min-w-11` son los 44 px de WCAG 2.5.5. Este objetivo lo pulsa un alumno con
      // prisa y el móvil en una mano.
      className={[
        'rounded-chip border-line text-text ease-hard flex min-h-11 min-w-11 items-center gap-2',
        'hover:bg-surface border-2 px-3 transition-transform duration-(--dur-tap) active:scale-95',
        className ?? '',
      ]
        .join(' ')
        .trim()}
      /*
       * **El código va DELANTE del nombre accesible, y no es adorno.**
       *
       * WCAG 2.5.3 (Label in Name, nivel A) exige que el nombre accesible contenga el texto que
       * se ve. Lo único que se ve aquí es «ES», y sustituirlo por «Idioma: Castellano…» deja a
       * quien maneja el ordenador por voz sin poder pulsarlo: lee «ES» en pantalla, dice «pulsa
       * ES» y no pasa nada. `axe` no comprueba este criterio, así que el CI nunca lo vería.
       */
      aria-label={`${actual.toUpperCase()}. ${t('language.label')}: ${t(`language.${actual}`)}. ${t(
        'language.switchTo',
        { language: t(`language.${siguiente}`) },
      )}`}
    >
      <Glifo nombre="idioma" size={18} />
      {/* El código en versales: tres letras menos que el nombre y se reconoce igual. Va
          `aria-hidden` porque el nombre accesible del botón ya lo dice con todas las letras. */}
      <span aria-hidden="true" className="text-micro font-display font-bold">
        {actual.toUpperCase()}
      </span>
    </button>
  );
}
